import React, { useState, useEffect, useRef, useCallback } from 'react';
import './TrailJump.css';

// Components
import TopBar from "../../Components/Games/TopBar";
import ResultPage from "../../Components/Games/ResultPage";
import GameIntroScreen from "../../Components/Games/GameIntroScreen";
import StartGame from "../../Components/Games/StartGame";

// Assets
import overlay from "../../assets/img/overlay.png";
import trailJumpIcon from '../../assets/img/icon-trail-jump.png';
import { lifestyleApi } from '../../apiService/api';
import { getUrlParams } from '../../apiService/timespam';
import { useGameBackNavigation } from "../../Components/Games/useGameBackNavigation";

const GAME_DURATION = 60;
const BASE_SPEED = 0.5;
const SPEED_INCREMENT = 0.15;
const TOLERANCE = 12;

const PHRASES = [
  "LOCK IN!", "KEEP GOING!", "PRECISION!", "FASTER!",
  "DON'T BLINK!", "ELITE FOCUS!", "CHAMPION!", "STAY SHARP!"
];

const trailJumpTutorialSteps = [
  {
    icon: "👀",
    title: "WATCH",
    desc: "Observe the rotating needle carefully."
  },
  {
    icon: "🎯",
    title: "TARGET",
    desc: "A glowing dot appears on the circle."
  },
  {
    icon: "👇",
    title: "TAP",
    desc: "Tap exactly when the needle reaches the dot."
  },
  {
    icon: "⚡",
    title: "BE PRECISE",
    desc: "Perfect timing gives higher score and streaks."
  }
];

const TrailJump2 = () => {
  useGameBackNavigation();
  const [phase, setPhase] = useState('INTRO');
  const [score, setScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [checkpointAngle, setCheckpointAngle] = useState(90);
  const [dotSize, setDotSize] = useState(24);
  const [phrase, setPhrase] = useState("READY?");

  const [correctMatches, setCorrectMatches] = useState(0);
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [cid, setCid] = useState("");
  const [groupName, setGroupName] = useState("");
  const [gameStateApi, setGameStateApi] = useState("initial");
  const [lastInsertedId, setLastInsertedId] = useState(null);
  const [goqiiBest, setGoqiiBest] = useState(0);
  const [backendCognitiveScore, setBackendCognitiveScore] = useState(null);
  const [backendSkill, setBackendSkill] = useState("");
  const [fastResponses, setFastResponses] = useState(0);
  const [weightages, setWeightages] = useState({ attemptWeightage: 1, accuracyWeightage: 1, streakWeightage: 1, fastWeightage: 1, scoreWeightage: 1, baseQuestionsAttempt: 0, correctMinQuestion: 1, streakMinQuestion: 1, fastResponseMinQuestion: 1, scorePoints: 1 });
  const [configError, setConfigError] = useState(false);

  const [tutorialStep, setTutorialStep] = useState(0);

  const requestRef = useRef();
  const angleRef = useRef(0);
  const speedRef = useRef(BASE_SPEED);
  const timerRef = useRef(null);
  const lastInsertedIdRef = useRef(null);
  const fastResponseRef = useRef(0); // Add this near your other refs

  const spawnCheckpoint = useCallback(() => {
    const minDistance = 70;
    const randomAdd = Math.floor(Math.random() * 180) + minDistance;
    setCheckpointAngle((angleRef.current + randomAdd) % 360);
    const newSize = Math.floor(Math.random() * (36 - 14 + 1)) + 14;
    setDotSize(newSize);
  }, []);

  const ELITE_TOLERANCE = 5;

  const handleTap = useCallback((e) => {
    if (phase !== 'GAME') return;
    const currentA = angleRef.current % 360;
    const diff = Math.abs(currentA - checkpointAngle);
    const normalizedDiff = Math.min(diff, 360 - diff);

    if (normalizedDiff < TOLERANCE) {
      let fastBonus = 0;
      if (normalizedDiff <= ELITE_TOLERANCE) {
        fastResponseRef.current += 1;
        // 2. Update State (For the UI/Result Page)
        setFastResponses(prev => prev + 1);
        fastBonus = weightages.fastWeightage * 10;
      }

      // currentStreak here is the streak count BEFORE this correct tap
      const streakBonus = weightages.streakWeightage * 10 * currentStreak;
      const gained = weightages.scorePoints + streakBonus + fastBonus;
      setScore(s => Math.max(0, s + gained));
      setCorrectMatches(prev => prev + 1);
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      setMaxStreak(prev => Math.max(prev, newStreak));

      setPhrase(PHRASES[Math.floor(Math.random() * PHRASES.length)]);
      speedRef.current += SPEED_INCREMENT;
      spawnCheckpoint();
    } else {
      setPhrase("MISS!");
      setWrongAttempts(prev => prev + 1);
      setCurrentStreak(0);
      setScore(s => Math.max(0, s - weightages.scorePoints / 2));
    }
  }, [phase, checkpointAngle, spawnCheckpoint, currentStreak, weightages]);

  const update = useCallback(() => {
    if (phase === 'GAME') {
      angleRef.current = (angleRef.current + speedRef.current) % 360;
      const needle = document.getElementById('trail-jump-needle-id');
      if (needle) needle.style.transform = `rotate(${angleRef.current}deg)`;
      requestRef.current = requestAnimationFrame(update);
    }
  }, [phase]);
  const fetchGameConfig = () => {
    setConfigError(false);
    const { encryptedId, gid } = getUrlParams();

    lifestyleApi.fetchGameDetails(encryptedId, gid)
      .then(res => {
        if (res.data?.code === 200) {
          const d = res.data.data;
          setBestScore(d.bestScore);
          setGoqiiBest(d.goqiiBest);
          setWeightages({
            attemptWeightage: d.attemptWeightage ?? 1,
            accuracyWeightage: d.accuracyWeightage ?? 1,
            streakWeightage: d.streakWeightage ?? 1,
            fastWeightage: d.fastWeightage ?? 1,
            scoreWeightage: d.scoreWeightage ?? 1,
            baseQuestionsAttempt: d.baseQuestionsAttempt || 0,
            correctMinQuestion: d.correctMinQuestion || 1,
            streakMinQuestion: d.streakMinQuestion || 1,
            fastResponseMinQuestion: d.fastResponseMinQuestion || 1,
            scorePoints: d.scorePoints || 1,
          });
        } else {
          setConfigError(true);
        }
      })
      .catch(err => {
        console.error("fetchGameDetails failed", err);
        setConfigError(true);
      });
  };

  useEffect(() => {
    fetchGameConfig();
  }, []);

  const startActualGame = async () => {
    const { encryptedId, gid } = getUrlParams();

    try {
      const res = await lifestyleApi.initialGameScore(encryptedId, gid);

      if (res.data?.code === 200) {
        const data = res.data.data;

        setCid(data.cid);
        setGroupName(data.groupName);
        setLastInsertedId(data.lastInsertedId);
        lastInsertedIdRef.current = data.lastInsertedId;
        setGameStateApi("initial");

      }
    } catch (err) {
      console.error("initialGameScore failed", err);
    }
    setScore(0);
    setCorrectMatches(0);
    setWrongAttempts(0);
    setCurrentStreak(0);
    setMaxStreak(0);
    setTimeLeft(GAME_DURATION);
    speedRef.current = BASE_SPEED;
    angleRef.current = 0;
    setPhase('GAME');
    setPhrase("GO!");
    spawnCheckpoint();
  };

  useEffect(() => {
    if (phase === 'GAME') {
      requestRef.current = requestAnimationFrame(update);
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setBestScore(old => Math.max(old, score));
            setPhase('RESULT');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => {
        cancelAnimationFrame(requestRef.current);
        clearInterval(timerRef.current);
      };
    }
  }, [phase, update, score]);

  useEffect(() => {
    const handleKeyDown = (e) => { if (e.code === 'Space') handleTap(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleTap]);

  const saveFinalGameScore = async () => {
    const { encryptedId, gid } = getUrlParams();
    const goqiiUserId = encryptedId;
    const totalQuestions = correctMatches + wrongAttempts;

    const payload = {
      goqiiUserId,
      gid,
      totalQuestions,
      correctAnswers: correctMatches,
      wrongAnswers: wrongAttempts,
      longestStreak: maxStreak,
      fastResponse: fastResponseRef.current,
      score,
      cid,
      lastInsertedId: lastInsertedIdRef.current,
    };

    try {
      const res = await lifestyleApi.saveGameScore(
        payload.goqiiUserId,
        payload.gid,
        payload.totalQuestions,
        payload.correctAnswers,
        payload.wrongAnswers,
        payload.longestStreak,
        payload.fastResponse,
        payload.score,
        payload.cid,
        payload.lastInsertedId
      );

      if (res?.data?.data?.cognitiveScore !== undefined) {
        setBackendCognitiveScore(res.data.data.cognitiveScore);
      }

      if (res?.data?.data?.skill) {
        setBackendSkill(res.data.data.skill);
      }
    } catch (err) {
      console.error("saveGameScore failed", err);
    }
  };

  useEffect(() => {
    if (phase === 'RESULT') {
      saveFinalGameScore();
    }
  }, [phase]);

  const getQuote = () => {
    if (backendCognitiveScore >= 90) return "Elite focus! Your timing is world-class.";
    if (backendCognitiveScore >= 65) return "Great precision! Your reflexes are sharp.";
    if (backendCognitiveScore >= 50) return "Good job! Keep training your focus.";
    if (backendCognitiveScore >= 20) return "Nice effort. Consistency will make you better.";
    return "Don’t worry. Every attempt trains your brain.";
  };


  return (
    <div
      className={`trail-jump-wrapper ${timeLeft < 20 && phase === 'GAME' ? 'trail-jump-critical' : ''}`}
      onClick={handleTap}
      style={{ '--dynamic-dot-size': `${dotSize}px` }}
    >
      {configError && (
        <div className="api-error-overlay">
          <div className="api-error-card">
            <div className="api-error-title">Something went wrong</div>
            <div className="api-error-text">There is a server error to open this game. Please try again after some time.</div>
            <button className="api-error-retry-btn" onClick={fetchGameConfig}>Retry</button>
          </div>
        </div>
      )}

      {/* TopBar is absolute or at the top of the flex column */}
      {phase === 'GAME' && (
        <TopBar score={score} timeLeft={timeLeft} />
      )}

      {/* Main Container for Game Elements to stay centered */}
      <div className="trail-jump-main-container">
        {phase === 'INTRO' && (
          <GameIntroScreen
            backgroundImage={overlay}
            gameLogo={trailJumpIcon}
            title="Trail Jump"
            onStart={() => setPhase('BENEFITS')}
          />
        )}

        {phase === 'BENEFITS' && (
          <StartGame
            backgroundImage={overlay}
            hbHeading=" Spatial Awareness"
            hbDescription="Fast-paced tracking games help your brain filter distractions and improve motor response timing."
            yourBest={bestScore}
            goqiiBest={goqiiBest}
            onStart={() => setPhase('TUTORIAL')}
          />
        )}

        {phase === 'TUTORIAL' && (
  <div className="trailjump trailjump-app-container">

    <div className="trailjump-tutorial-frame">

      <div className="trailjump-tutorial-header">

        <div className="trailjump-tutorial-label">
          TUTORIAL
        </div>

        <button
          className="trailjump-tutorial-skip"
          onClick={() => {
            setPhase('STARTING');
            setTimeout(() => startActualGame(), 1200);
          }}
        >
          Skip ▶
        </button>

      </div>

      <div className="trailjump-tutorial-instruction">
        Step {tutorialStep + 1}: {trailJumpTutorialSteps[tutorialStep].title}
      </div>

      <div className="trailjump-tutorial-icon">
        {trailJumpTutorialSteps[tutorialStep].icon}
      </div>

      <p className="trailjump-tutorial-desc">
        {trailJumpTutorialSteps[tutorialStep].desc}
      </p>

      {tutorialStep < trailJumpTutorialSteps.length - 1 ? (
        <button
          className="trailjump-tutorial-btn"
          onClick={() => setTutorialStep(tutorialStep + 1)}
        >
          Next
        </button>
      ) : (
        <button
          className="trailjump-tutorial-btn"
          onClick={() => {
            setPhase('STARTING');
            setTimeout(() => startActualGame(), 1200);
          }}
        >
          Start Game
        </button>
      )}

    </div>

  </div>
)}

{phase === 'STARTING' && (
  <div className="trailjump-start-overlay">
    <div className="trailjump-start-text">START!</div>
  </div>
)}

        {phase === 'GAME' && (
          <>
            <div className="trail-jump-phrase-box">{phrase}</div>

            <div className="trail-jump-game-area">
              <div className="trail-jump-track-circle"></div>
              <div
                className="trail-jump-checkpoint-dot"
                style={{ transform: `rotate(${checkpointAngle}deg)` }}
              ></div>
              <div id="trail-jump-needle-id" className="trail-jump-needle-line">
                <div className="trail-jump-needle-tip"></div>
              </div>
            </div>
          </>
        )}

        {phase === 'RESULT' && (
          <ResultPage
            visible={true}
            score={score}
            best={bestScore}
            correct={correctMatches}
            wrong={wrongAttempts}
            longestStreak={maxStreak}
            fastResponses={fastResponses}
            cognitiveScore={backendCognitiveScore}
            domainLabel={backendSkill}
            quote={getQuote()}
            onReplay={() => setPhase('BENEFITS')}
          />
        )}
      </div>
    </div>
  );
};

export default TrailJump2;